import {
  Button,
  FileUpload,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';

import { useNotifications } from '../../features/notifications';

function filesToTokens(files = []) {
  return files.map((file) => {
    const {
      name,
      ref: fileRef,
      type,
      lastModifiedTS,
      size,
    } = file;
    const fileName = name || fileRef?.split('/').pop() || 'Uploaded file';

    const token = new File([new Blob([''], { type })], fileName, {
      type,
      lastModified: lastModifiedTS,
    });
    // File.size is read-only and derived from the (empty) blob, so override it
    // to show the stored size without holding the bytes in memory. Files saved
    // before `size` was recorded fall back to 0 bytes.
    Object.defineProperty(token, 'size', { value: size || 0 });
    return token;
  });
}

function FileUploader({
  uploadPath,
  uploadedFiles,
  uploadErrors,
  onEmpty,
  onUpload,
  accept = '.pdf, .doc, .docx',
  constraintText = 'Please upload a PDF or Word document',
  tokenLimit = 1,
  fileTypeName = 'File',
}) {
  const { addNotification } = useNotifications();

  // Derived from the prop: every upload/removal round-trips through
  // onUpload/onEmpty, so the parent-owned `uploadedFiles` is the single
  // source of truth for the token list.
  const selectedFiles = useMemo(() => filesToTokens(uploadedFiles), [uploadedFiles]);
  const [uploading, setUploading] = useState(false);

  // convert uploadErrors to array of strings
  const fileErrors = useMemo(() => {
    // Check if the input is an array of objects
    if (Array.isArray(uploadErrors) && uploadErrors.length > 0 && typeof uploadErrors[0] === 'object') {
      // Convert array of objects to array of strings
      return uploadErrors.map((errorObj) => {
        const errorStringArr = Object.entries(errorObj).map(([key, value]) => `${key} - ${value}`);
        return errorStringArr.join('; ');
      });
    }
    // Return the array as is if it's already an array of strings
    return uploadErrors;
  }, [uploadErrors]);

  const handleFileUploadChange = useCallback(({ detail }) => {
    if (!detail.value?.length) {
      onEmpty();
      return;
    }

    setUploading(true);
    // Upload into a unique timestamped subfolder instead of overwriting the
    // file at `${uploadPath}/${file.name}`: overwriting an existing Storage
    // object rotates its download token, which silently breaks downloadURLs
    // already saved on Firestore docs (e.g. re-upload a same-named file, then
    // cancel the form without saving). Unique paths keep old URLs valid
    // forever; replaced files are simply orphaned in Storage. A subfolder
    // (rather than a filename prefix) keeps `ref.split('/').pop()` usable as
    // the display-name fallback in filesToTokens.
    const uploadTS = Date.now();
    const uploadPromises = detail.value.map(async (file) => {
      const filePath = `${uploadPath}/${uploadTS}/${file.name}`;
      const fileRef = ref(getStorage(), filePath);
      try {
        // Upload file
        await uploadBytes(fileRef, file);

        // Get download URL
        const downloadURL = await getDownloadURL(fileRef);
        // return data in the format we want to save
        return {
          downloadURL,
          lastModifiedTS: file.lastModified,
          name: file.name,
          ref: filePath,
          size: file.size,
          type: file.type,
        };
      } catch (error) {
        addNotification({
          type: 'error',
          dismissible: true,
          content: `Error uploading file to firebase: ${error.message}`,
          id: 'file-upload-error',
        });
        return {};
      }
    });

    Promise.all(uploadPromises)
      .then((data) => {
        onUpload(data);
      })
      .finally(() => {
        setUploading(false);
      });
  }, [onEmpty, uploadPath, addNotification, onUpload]);

  return (
    <SpaceBetween size="xs">
      {uploading && <Spinner size="large" />}
      <FileUpload
        i18nStrings={{
          uploadButtonText: (e) => (e ? 'Choose files' : 'Choose a file'),
          dropzoneText: (e) => (e
            ? 'Drop files to upload'
            : 'Drop file to upload'),
          removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
          limitShowFewer: 'Show fewer files',
          limitShowMore: 'Show more files',
          errorIconAriaLabel: 'Error',
        }}
        accept={accept}
        onChange={handleFileUploadChange}
        value={selectedFiles}
        showFileLastModified
        showFileSize
        showFileThumbnail
        tokenLimit={tokenLimit}
        fileErrors={fileErrors}
        constraintText={constraintText}
      />
      {uploadedFiles?.[0]?.downloadURL && (
        <Button
          href={uploadedFiles[0].downloadURL}
          target="_blank"
          iconName="external"
          iconAlign="right"
        >
          {`${fileTypeName} Details`}
        </Button>
      )}
    </SpaceBetween>
  );
}
FileUploader.propTypes = {
  uploadPath: PropTypes.string.isRequired,
  uploadedFiles: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    lastModifiedTS: PropTypes.number,
    size: PropTypes.number,
    downloadURL: PropTypes.string,
  })).isRequired,
  uploadErrors: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.object),
  ]).isRequired,
  onEmpty: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  accept: PropTypes.string,
  constraintText: PropTypes.string,
  tokenLimit: PropTypes.number,
  fileTypeName: PropTypes.string,
};

export default FileUploader;
